/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Text,
  Button,
  ModalConfirm,
  Table,
  Loader,
  theme,
} from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { ServiceShortInitialState } from "~/components/Services/initialState";
import { showSuccess } from "~/components/Toast";
import { ServiceCmtSmall } from "~/models";
import {
  ARAttachedServices,
  AssignmentRuleRouteParams,
} from "~/models/assignmentRule";
import { ServiceGroup } from "~/models/serviceGroup";
import { ROUTES } from "~/routing";

import { useAxios, generateActionSuccessText } from "~/tools";

type ARUnassignServiceProps = {
  service: ARAttachedServices["service"];
  refetch: () => void;
};

function ARUnassignService({ service, refetch }: ARUnassignServiceProps) {
  const {
    customerId,
    assignmentRuleId,
  } = useParams<AssignmentRuleRouteParams>();

  const [isOpen, setIsOpen] = React.useState(false);
  const [serviceGroups, setServiceGroups] = React.useState<ServiceGroup[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [page, setPage] = React.useState(0);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const isServiceGroup = !!service.serviceGroup;

  const fetchServiceGroup = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/catalog/serviceGroups?limit=${50}&page=${page}`
      );
      data.totalPages > 1 && data.totalPages > page + 1 && setPage(page + 1);
      data.serviceGroups &&
        setServiceGroups((prevState: ServiceGroup[]) => [
          ...prevState,
          ...data.serviceGroups,
        ]);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, page, showError]);

  React.useEffect(() => {
    isOpen && fetchServiceGroup();
  }, [fetchServiceGroup, isOpen]);
  const findServiceGroup =
    serviceGroups?.find(
      (s: ServiceGroup) => s.id === service?.serviceGroup?.id
    ) || ServiceShortInitialState;

  const nameForToast = isServiceGroup
    ? service?.serviceGroup?.name
    : service.name;

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        css={{ backgroundColor: theme.color.background.ui01, width: "100%" }}
        size="md"
        icon="brokenLink"
        severity="low"
      >
        Unassign
      </Button>
      <ModalConfirm
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        confirm={async () => {
          try {
            isServiceGroup
              ? await Axios().delete(
                  `/customers/${customerId}/assignmentRules/${assignmentRuleId}/serviceGroups/${service?.serviceGroup?.id}`
                )
              : await Axios().delete(
                  `/customers/${customerId}/assignmentRules/${assignmentRuleId}/services/${service?.id}`
                );
            setIsOpen(true);
            refetch();
            showSuccess(
              generateActionSuccessText(`The ${nameForToast}`)()("detached")()
            );
          } catch (e) {
            showError(e.response);
          }
          setIsOpen(false);
        }}
        actionLabel="Unassign"
        contentLabel={`Unassign ${
          isServiceGroup ? "Service Group" : "Service"
        }`}
      >
        <Text color="danger">
          {`Are you sure you want to unassign ${nameForToast}? Any information about modified parameters will be lost.`}
        </Text>
        {isServiceGroup && (
          <>
            <Text tag="div">List of services that will be unassigned:</Text>
            <div css={{ height: "10rem" }}>
              <Table tiny>
                <Table.thead>
                  <Table.tr>
                    <Table.th css={{ width: "100%" }}>Service Name</Table.th>
                  </Table.tr>
                </Table.thead>
                <Table.tbody>
                  {isLoading ? (
                    <Table.tr>
                      <Table.td css={{ width: "100%" }}>
                        <Loader inContent />
                      </Table.td>
                    </Table.tr>
                  ) : (
                    findServiceGroup?.services?.map(
                      (serviceItem: ServiceCmtSmall) => (
                        <Table.tr key={serviceItem.id}>
                          <Table.td css={{ width: "100%" }}>
                            <Link
                              to={`${ROUTES.serviceCommitments.index}/${serviceItem.id}`}
                            >
                              {serviceItem.name}
                            </Link>
                          </Table.td>
                        </Table.tr>
                      )
                    )
                  )}
                </Table.tbody>
              </Table>
            </div>
          </>
        )}
      </ModalConfirm>
    </>
  );
}

export { ARUnassignService };
